// Where a stored submission stands with the visitor's confirmation, read
// live by submission id: the pure reading of the server's answers and the
// decision of what to ask next. The requests themselves are the injected
// SubmissionStatusClient, so every branch is tested without a network.

import { z } from "zod";

import {
  correctionProofOf,
  type CorrectionProof,
  type GrantLookup,
} from "pages/Login/login-grant-flow";
import type { ResumeNext } from "pages/StartResume/resume";

import type {
  ConfirmationStatus,
  GrantMethod,
  LeadResult,
  ResultLanding,
} from "./submit";

const statusAnswerSchema = z.object({
  status: z.enum([
    "awaiting_confirmation",
    "confirmed",
    "completed",
    "expired",
    "closed",
  ]),
  landing: z.object({
    kind: z.string(),
    quote_id: z.string().nullable(),
    case_id: z.string().nullable(),
    event_id: z.string().nullable(),
  }),
});

export type SubmissionStatus = {
  status: ConfirmationStatus;
  landing: ResultLanding | null;
};

function landingOf(
  answer: z.infer<typeof statusAnswerSchema>["landing"],
): ResultLanding {
  const { kind, quote_id, case_id, event_id } = answer;
  const base = { quoteId: quote_id, caseId: case_id, eventId: event_id };
  if (kind === "quote" && quote_id) return { ...base, kind: "quote" };
  if (kind === "case" && case_id) return { ...base, kind: "case" };
  return { ...base, kind: "other" };
}

/** The status route's answer, or null when it is not one. The landing is
 * kept only for a submission that is confirmed or completed. */
export function parseSubmissionStatus(raw: unknown): SubmissionStatus | null {
  const parsed = statusAnswerSchema.safeParse(raw);
  if (!parsed.success) return null;
  const { status, landing } = parsed.data;
  const landed = status === "confirmed" || status === "completed";
  return { status, landing: landed ? landingOf(landing) : null };
}

export type MintedGrant = {
  grant: string;
  methods: GrantMethod[];
  phoneDisplay: string | null;
  emailDisplay: string | null;
  /** What lets the visitor correct the destinations: the signed link this
   * browser arrived on, or the ZIP code from the form; null when neither. */
  correctionProof: CorrectionProof;
  /** The grant was asked for with a handoff, so its destinations may be
   * shown as typed. */
  unmasked?: boolean;
};

const mintedGrantSchema = z.object({
  login_grant: z.string().regex(/^[0-9a-f]{64}$/),
  methods: z.array(z.enum(["sms_otp", "email_link"])).min(1),
  destinations: z.object({
    phone_display: z.string().nullable(),
    email_display: z.string().nullable(),
  }),
  correction_proof: z.unknown().optional().transform(correctionProofOf),
});

/** The grant route's answer, or null when it is not one. */
export function parseMintedGrant(raw: unknown): MintedGrant | null {
  const parsed = mintedGrantSchema.safeParse(raw);
  if (!parsed.success) return null;
  const { login_grant, methods, destinations, correction_proof } = parsed.data;
  return {
    grant: login_grant,
    methods,
    phoneDisplay: destinations.phone_display,
    emailDisplay: destinations.email_display,
    correctionProof: correction_proof,
  };
}

const confirmedMintSchema = z.object({
  confirmed: z.literal(true),
  next: z.object({
    kind: z.string(),
    quote_id: z.string().nullable(),
    case_id: z.string().nullable(),
    event_id: z.string().nullable(),
  }),
});

/** Where the grant route says a visitor goes when their remembered browser
 * confirmed the submission instead of being given a grant, or null when the
 * answer is not that. */
export function parseConfirmedMint(raw: unknown): ResumeNext | null {
  const parsed = confirmedMintSchema.safeParse(raw);
  return parsed.success ? parsed.data.next : null;
}

/** The grant as a stored result keeps it, or null for a grant whose
 * destinations were shown as typed: a stored result is read back later, by
 * anyone on the device, and neither those destinations nor a grant that
 * would reveal them again is kept. The landing then mints a fresh one. */
export function verificationOf(grant: MintedGrant): LeadResult["verification"] {
  if (grant.unmasked === true) return null;
  return {
    grant: grant.grant,
    methods: grant.methods,
    phoneDisplay: grant.phoneDisplay,
    emailDisplay: grant.emailDisplay,
  };
}

export type ConfirmedVia = "signed_in" | "device";

/** What confirmed the submission on the grant route: the visitor's own
 * sign-in when the answer says so, otherwise the remembered browser. */
export function parseConfirmedVia(raw: unknown): ConfirmedVia {
  const via =
    typeof raw === "object" && raw !== null
      ? (raw as Record<string, unknown>).confirmed_via
      : null;
  return via === "signed_in" ? "signed_in" : "device";
}

/** Why the sign-in confirmation was refused. `expired` and `closed` are
 * only ever said to the Person the submission matched; every other refusal,
 * and any reason this version does not know, is `not_found`. */
export type ConfirmRefusal = "expired" | "closed" | "not_found";

/** What a sign-in confirmation that did not confirm answered: a refusal
 * for good, or `pending` when the submission is still being finished and the
 * same request can be sent again. */
export type SignInRefusal = ConfirmRefusal | "pending";

/** The refusal a sign-in confirmation answered, or null when the answer is
 * not one. A refusal that names no known reason reads as `not_found`. */
export function parseConfirmRefusal(raw: unknown): SignInRefusal | null {
  if (typeof raw !== "object" || raw === null) return null;
  const body = raw as Record<string, unknown>;
  if (body.confirmed !== false) return null;
  return body.reason === "expired" ||
    body.reason === "closed" ||
    body.reason === "pending"
    ? body.reason
    : "not_found";
}

export type StatusAnswer =
  | { kind: "found"; status: SubmissionStatus }
  | { kind: "missing" }
  | { kind: "failed" };

export type LookupAnswer =
  | { kind: "valid"; lookup: GrantLookup }
  | { kind: "invalid" }
  | { kind: "failed" };

export type MintAnswer =
  | { kind: "minted"; grant: MintedGrant }
  /** The visitor's sign-in or remembered browser confirmed the submission;
   * no grant was minted. */
  | { kind: "confirmed"; next: ResumeNext; via: ConfirmedVia }
  | { kind: "unavailable" }
  | { kind: "missing" }
  /** The server asked to wait before another grant is minted. */
  | { kind: "rate_limited" }
  | { kind: "failed" };

export type SignInConfirmAnswer =
  | { kind: "confirmed"; next: ResumeNext }
  /** The server refused, and said why. */
  | { kind: "refused"; reason: ConfirmRefusal }
  /** The submission is still being finished; asking again may confirm it. */
  | { kind: "pending" }
  /** The visitor is signed out, or the request failed. */
  | { kind: "not_confirmed" };

export interface SignInConfirmClient {
  signInConfirm(submissionId: string): Promise<SignInConfirmAnswer>;
}

export interface SubmissionStatusClient {
  status(submissionId: string): Promise<StatusAnswer>;
  lookup(grant: string): Promise<LookupAnswer>;
  mint(submissionId: string): Promise<MintAnswer>;
}

export type SubmissionStatusOutcome =
  | { kind: "unchanged" }
  /** The result to store and show. */
  | { kind: "updated"; result: LeadResult }
  /** Still awaiting confirmation, but no usable grant could be had. */
  | { kind: "retry"; result: LeadResult };

function awaiting(
  result: LeadResult,
  verification: LeadResult["verification"],
): LeadResult {
  return {
    ...result,
    status: "awaiting_confirmation",
    landing: null,
    verification,
  };
}

function expired(result: LeadResult): LeadResult {
  return { ...result, status: "expired", landing: null, verification: null };
}

function closed(result: LeadResult): LeadResult {
  return { ...result, status: "closed", landing: null, verification: null };
}

function hasIds(landing: ResultLanding | null): boolean {
  return (
    landing !== null && (landing.quoteId !== null || landing.caseId !== null)
  );
}

/** The server gives a landing's ids only to the signed-in Person the
 * submission is about; to anyone else it answers the kind with no ids. The
 * ids already stored with this result came from the submission itself, so an
 * answer without ids never replaces them. */
function landingAfter(
  result: LeadResult,
  answered: ResultLanding | null,
): ResultLanding | null {
  if (hasIds(answered) || !hasIds(result.landing)) return answered;
  return result.landing;
}

function settled(result: LeadResult, status: SubmissionStatus): LeadResult {
  if (status.status === "awaiting_confirmation") return awaiting(result, null);
  if (status.status === "expired") return expired(result);
  if (status.status === "closed") return closed(result);
  return {
    ...result,
    status: status.status,
    landing: landingAfter(result, status.landing),
    verification: null,
  };
}

async function freshGrant(
  result: LeadResult,
  submissionId: string,
  client: SubmissionStatusClient,
): Promise<SubmissionStatusOutcome> {
  const minted = await client.mint(submissionId);
  switch (minted.kind) {
    case "confirmed":
      return {
        kind: "updated",
        result: {
          ...result,
          status: "confirmed",
          landing: landingAfter(result, landingOf(minted.next)),
          verification: null,
        },
      };
    case "minted":
      return {
        kind: "updated",
        result: awaiting(result, verificationOf(minted.grant)),
      };
    case "unavailable":
    case "missing":
      return { kind: "updated", result: expired(result) };
    case "failed":
    case "rate_limited":
      return { kind: "retry", result: awaiting(result, null) };
  }
}

/**
 * Brings a stored result up to date with the server. A submission that is
 * confirmed, completed, expired or closed is stored as such. One still awaiting
 * confirmation keeps its grant while the grant still works (and takes the
 * methods the server now says it offers); otherwise a fresh grant is minted
 * for the submission. A submission the server no longer has, or can no
 * longer mint for, has expired.
 */
export async function resolveSubmissionStatus(
  result: LeadResult,
  client: SubmissionStatusClient,
): Promise<SubmissionStatusOutcome> {
  const submissionId = result.submissionId;
  if (submissionId === null) return { kind: "unchanged" };

  const status = await client.status(submissionId);
  if (status.kind === "failed") return { kind: "unchanged" };
  if (status.kind === "missing") {
    return result.status === "awaiting_confirmation"
      ? { kind: "updated", result: expired(result) }
      : { kind: "unchanged" };
  }
  if (status.status.status !== "awaiting_confirmation") {
    return { kind: "updated", result: settled(result, status.status) };
  }

  const held = result.verification;
  if (held === null) return freshGrant(result, submissionId, client);

  const lookup = await client.lookup(held.grant);
  if (lookup.kind === "invalid") {
    return freshGrant(result, submissionId, client);
  }
  if (lookup.kind === "failed") {
    return { kind: "updated", result: awaiting(result, held) };
  }
  return {
    kind: "updated",
    result: awaiting(result, {
      grant: held.grant,
      methods: lookup.lookup.methods,
      phoneDisplay: lookup.lookup.phoneDisplay,
      emailDisplay: lookup.lookup.emailDisplay,
    }),
  };
}
